import { Button, Col, Row, Select } from 'antd';
import { RangeDatePicker } from 'modules/common/components';
import {
  ReportDateRange,
  ReportFilterValues,
} from 'modules/reports/hooks/useReportFilters';
import { ReportFilterConfig } from 'modules/reports/types';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';

type ReportFilterBarProps<T> = {
  configs: ReportFilterConfig<T>[];
  values: ReportFilterValues;
  optionsByKey: Record<string, string[]>;
  onChange: (
    key: string,
    value: string[] | ReportDateRange | undefined
  ) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
  /** Row counts shown once the dataset is loaded */
  counts?: { shown: number; total: number };
};

export function ReportFilterBar<T>({
  configs,
  values,
  optionsByKey,
  onChange,
  onClear,
  hasActiveFilters,
  counts,
}: ReportFilterBarProps<T>) {
  const { t } = useTranslation('reports.ReportFilters');

  const handleRangeChange = (
    key: string,
    dates: [Moment | null, Moment | null] | null
  ) => {
    const [from, to] = dates ?? [null, null];
    if (!from && !to) {
      onChange(key, undefined);
      return;
    }
    onChange(key, [
      from ? from.startOf('day').toISOString() : null,
      to ? to.endOf('day').toISOString() : null,
    ]);
  };

  return (
    <Row gutter={[8, 8]} align="bottom">
      {configs.map((config) => {
        if (config.type === 'dateRange') {
          const range = (values[config.key] as ReportDateRange) ?? [null, null];
          const value: [Moment | null, Moment | null] | null =
            range[0] || range[1]
              ? [
                  range[0] ? moment(range[0]) : null,
                  range[1] ? moment(range[1]) : null,
                ]
              : null;
          return (
            <Col key={config.key}>
              <span className={styles.filterLabel}>{config.label}</span>
              <RangeDatePicker
                value={value as any}
                onChange={
                  ((dates: [Moment | null, Moment | null] | null) =>
                    handleRangeChange(config.key, dates)) as any
                }
                placeholder={[t('from'), t('to')]}
                allowEmpty={[true, true]}
              />
            </Col>
          );
        }

        return (
          <Col key={config.key} style={{ minWidth: 180 }}>
            <span className={styles.filterLabel}>{config.label}</span>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              style={{ width: '100%' }}
              placeholder={config.label}
              options={(optionsByKey[config.key] ?? []).map((option) => ({
                value: option,
                label: option,
              }))}
              value={(values[config.key] as string[]) ?? []}
              onChange={(selected: string[]) =>
                onChange(config.key, selected.length ? selected : undefined)
              }
            />
          </Col>
        );
      })}
      {hasActiveFilters && (
        <Col>
          <Button type="link" onClick={onClear}>
            {t('clearFilters')}
          </Button>
        </Col>
      )}
      {counts && (
        <Col>
          <span>
            {t('rowsShown', { shown: counts.shown, total: counts.total })}
          </span>
        </Col>
      )}
    </Row>
  );
}
